/**
 * IntegrationStatusPanel — surfaces People Engine connectivity to the user.
 *
 * Why this exists:
 *   When People is UNAVAILABLE / NOT_CONFIGURED, several downstream
 *   numbers (payroll obligations, service-charge distribution) are
 *   genuinely UNKNOWN. The system MUST NOT silently render them as ₹0 —
 *   that lies by omission. This panel makes absence visible.
 *
 * Data source: GET /api/accounts/cashflow/obligations
 *   integrations.people_payroll_status   ∈ ACTIVE | UNAVAILABLE | NOT_CONFIGURED
 *   integrations.people_sc_policy_status ∈ ACTIVE | UNAVAILABLE | NOT_CONFIGURED
 *   integrations.people_connected        : boolean
 *   summary.has_unknown_streams          : boolean
 *
 * Renders nothing when both streams are ACTIVE — no noise on the happy path.
 */
import React, { useEffect, useState } from 'react';
import { Card, CardContent } from './ui/card';
import { AlertTriangle, Plug, Info } from 'lucide-react';
import { accountsAPI } from '../lib/api';

const STATUS_TONE = {
  ACTIVE: { dot: 'bg-emerald-400', text: 'text-emerald-400', label: 'Connected' },
  CONNECTED: { dot: 'bg-emerald-400', text: 'text-emerald-400', label: 'Connected' },
  NOT_CONFIGURED: { dot: 'bg-amber-400', text: 'text-amber-300', label: 'Not configured' },
  UNAVAILABLE: { dot: 'bg-rose-400', text: 'text-rose-300', label: 'Not connected' },
};

const StatusPill = ({ status, label, testId }) => {
  const tone = STATUS_TONE[status] || STATUS_TONE.UNAVAILABLE;
  return (
    <div className="flex items-center gap-2 px-2.5 py-1 rounded-md bg-zinc-900/40 border border-zinc-800/60" data-testid={testId}>
      <span className={`w-1.5 h-1.5 rounded-full ${tone.dot}`} />
      <span className="text-[11px] text-zinc-400">{label}</span>
      <span className={`text-[11px] font-medium ${tone.text}`}>
        {tone.label}
      </span>
    </div>
  );
};

const IntegrationStatusPanel = () => {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const res = await accountsAPI.getObligations();
        if (alive) setData(res.data);
      } catch (e) {
        if (alive) setError(e.message || 'Could not fetch integration status');
      }
    })();
    return () => { alive = false; };
  }, []);

  if (error || !data) return null;

  const ints = data.integrations || {};
  const summary = data.summary || {};
  const payrollStatus = ints.people_payroll_status || 'UNAVAILABLE';
  const scStatus = ints.people_sc_policy_status || 'UNAVAILABLE';
  const allConnected = ints.people_connected === true;
  const hasUnknown = summary.has_unknown_streams === true;

  // Happy path: both streams ACTIVE → render nothing. Avoid noise.
  if (allConnected && !hasUnknown) return null;

  // Pull amount-vs-null from buckets so we can show "Known cash impact"
  // alongside "Hidden streams".
  const buckets = summary.by_source_type || {};
  const known = summary.total_outstanding_known;
  const hiddenStreams = Object.entries(buckets)
    .filter(([, v]) => v && v.connected === false)
    .map(([k]) => k);

  return (
    <Card
      className="border-rose-500/30 bg-gradient-to-br from-rose-950/30 to-zinc-900/60"
      data-testid="integration-status-panel"
    >
      <CardContent className="p-4">
        <div className="flex items-start gap-3">
          <Plug className="w-4 h-4 mt-0.5 text-rose-400 shrink-0" />
          <div className="flex-1 space-y-3">
            <div>
              <p className="text-sm font-medium text-rose-200" data-testid="integration-status-title">
                People Engine integration incomplete
              </p>
              <p className="text-[11px] text-rose-300/70 mt-0.5">
                Some treasury streams cannot be calculated until People exposes
                its policy. Numbers below are <span className="font-semibold">known-only</span> —
                they exclude the streams listed as "Not connected".
              </p>
            </div>

            <div className="flex flex-wrap gap-2">
              <StatusPill
                label="Payroll"
                status={payrollStatus}
                testId="integration-pill-payroll"
              />
              <StatusPill
                label="Service Charge Policy"
                status={scStatus}
                testId="integration-pill-sc-policy"
              />
            </div>

            {hiddenStreams.length > 0 && (
              <div
                className="flex items-start gap-2 px-3 py-2 rounded-md bg-zinc-950/40 border border-zinc-800/60 text-[11px] text-zinc-300"
                data-testid="integration-hidden-streams"
              >
                <Info className="w-3.5 h-3.5 mt-0.5 shrink-0 text-amber-400" />
                <div className="space-y-0.5">
                  <div>
                    <span className="text-zinc-400">Visible obligations:</span>{' '}
                    <span className="font-mono text-emerald-300">
                      {known !== null && known !== undefined
                        ? new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(known)
                        : '—'}
                    </span>{' '}
                    <span className="text-zinc-500">
                      (vendor bills only — payroll &amp; service-charge cycles unknown)
                    </span>
                  </div>
                  <div className="text-zinc-400">
                    Hidden streams: <span className="text-amber-300">{hiddenStreams.join(' · ')}</span>
                  </div>
                </div>
              </div>
            )}
          </div>
          <AlertTriangle className="w-4 h-4 text-rose-400 shrink-0" />
        </div>
      </CardContent>
    </Card>
  );
};

export default IntegrationStatusPanel;
